import React from 'react';
import { Shield, Award, Globe2 } from 'lucide-react';

/** SBT (Soulbound Token) certificate card for verified partners. */
const TIER_DETAIL = {
  essence: { label: 'Essence', accent: '#AAC6D4' },
  atelier: { label: 'Atelier', accent: '#90B0BF' },
  sanctum: { label: 'Sanctum', accent: '#7C9CB1' },
};

const SBTCertificate = ({ sbt, partner }) => {
  if (!sbt) {
    return (
      <div
        data-testid="sbt-empty"
        className="border border-dashed border-[#C1D7E2] p-10 text-center"
      >
        <Shield
          size={36}
          strokeWidth={1.2}
          className="mx-auto text-[#90B0BF] mb-4"
        />
        <p className="font-serif italic text-xl text-[#7C9CB1]">
          No certificate yet.
        </p>
        <p className="mt-2 font-mono text-[11px] tracking-[0.2em] uppercase text-[#90B0BF]">
          Your SBT is minted the moment you are approved.
        </p>
      </div>
    );
  }

  const tier = TIER_DETAIL[partner?.tier] || TIER_DETAIL.essence;

  return (
    <article
      data-testid="sbt-certificate"
      className="relative border border-[#C1D7E2] bg-[#F4F8F9] p-8 md:p-10 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -right-20 -top-20 w-80 h-80 rounded-full opacity-30 blur-3xl"
        style={{ background: tier.accent }}
      />
      <div className="relative grid grid-cols-1 md:grid-cols-12 gap-8">
        <div className="md:col-span-7">
          <p className="font-mono text-[10px] tracking-[0.35em] uppercase text-[#90B0BF]">
            <span className="label-divider" />
            Soulbound Certificate
          </p>
          <h3 className="mt-3 font-serif text-3xl md:text-4xl text-[#7C9CB1] font-light leading-tight">
            {partner?.name}
          </h3>
          <p className="mt-2 font-serif italic text-[#90B0BF]">
            Tier · {tier.label}
          </p>

          <dl className="mt-8 space-y-3 font-mono text-[11px] text-[#7C9CB1]">
            <Row label="Token ID" value={sbt.token_id} />
            <Row label="Chain hash" value={sbt.chain_hash} />
            <Row label="Metadata" value={sbt.metadata_uri} />
            <Row
              label="Issued"
              value={new Date(sbt.issued_at).toLocaleDateString()}
            />
            <Row label="Transferable" value="No · Soulbound" />
          </dl>
        </div>

        <div className="md:col-span-5 flex items-center justify-center">
          <div className="relative w-48 h-48">
            <div
              className="absolute inset-0 rounded-full border border-[#C1D7E2]"
              style={{
                background: `conic-gradient(from 200deg, ${tier.accent}, #F4F8F9, ${tier.accent})`,
                opacity: 0.9,
              }}
            />
            <div className="absolute inset-4 rounded-full bg-[#F4F8F9] border border-[#C1D7E2] flex flex-col items-center justify-center text-center px-4">
              <Award size={28} strokeWidth={1.3} className="text-[#7C9CB1]" />
              <p className="mt-2 font-mono text-[10px] tracking-[0.3em] uppercase text-[#90B0BF]">
                Verified
              </p>
              <p className="font-serif text-xl text-[#7C9CB1] font-light">
                {tier.label}
              </p>
              <p className="mt-1 font-mono text-[9px] tracking-[0.2em] text-[#90B0BF] flex items-center gap-1">
                <Globe2 size={9} strokeWidth={1.5} />
                Shreesavaa SBT
              </p>
            </div>
          </div>
        </div>
      </div>
    </article>
  );
};

const Row = ({ label, value }) => (
  <div className="flex items-baseline justify-between gap-6 border-b border-[#C1D7E2] pb-2">
    <dt className="text-[#90B0BF] tracking-[0.22em] uppercase text-[10px]">
      {label}
    </dt>
    <dd className="truncate text-right">{value}</dd>
  </div>
);

export default SBTCertificate;
